import React from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ButtonField from '../../Components/Ui/ButtonField';
import { useTailwindBrand } from '../../Components/shared';
import { getActiveClientBrandConfig } from '../../config/environment.config';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BannerVariant = 'action' | 'info' | 'success' | 'error';

export interface StatusBanner {
  variant: BannerVariant;
  title: string;
  message: string | React.ReactNode | null;
}

/** Où la bannière est affichée : popup de la liste ou fiche client dédiée. */
export type StatusBannerContext = 'row' | 'page';

interface BuildStatusBannersParams {
  description?: string | null;
  /** Envoi de l'e-mail de relance ; absent, le bouton n'est pas affiché. */
  onRelance?: () => void;
  primaryTextClass: string;
  context?: StatusBannerContext;
}

// ─── Bannières par statut ─────────────────────────────────────────────────────

export const buildStatusBanners = ({
  description,
  onRelance,
  primaryTextClass,
  context = 'row',
}: BuildStatusBannersParams): Partial<Record<string, StatusBanner>> => {
  const urssafActivationMessage = (
    <div className="w-full">
      <p className="mb-2">
        Votre {getActiveClientBrandConfig()} a rempli le formulaire mais <strong>n'a pas encore activé son compte URSSAF</strong>.
        Transmettez-lui les instructions ci-dessous ou envoyez-lui directement l'email de relance.
      </p>

      <div className="bg-white/60 border border-amber-100 rounded-md p-3 text-xs space-y-2 mb-3">
        <p className="font-semibold">✔️ Ce que votre élève doit faire</p>
        <p>
          Il a reçu un email d'activation depuis <strong>veuillez-ne-pas-repondre@urssaf.fr</strong>.<br />
          ➡️ Il doit ouvrir cet email et cliquer sur le lien d'activation.
        </p>
        <p>
          ❗ <span className="font-medium">Mail introuvable ou expiré ?</span><br />
          Il peut en demander un nouveau ici :{' '}
          <a href="https://www.particulier.urssaf.fr/#/lien-activation-compte" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-medium">
            particulier.urssaf.fr → lien d'activation
          </a>
        </p>

        <p className="font-semibold pt-1">🔐 Une fois le mail reçu :</p>

        <div>
          <p className="font-medium">➤ Cas 1 : Pas encore de compte URSSAF</p>
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            <li>Configurer son mot de passe via le lien reçu</li>
            <li>Se connecter (identifiant = adresse email)</li>
            <li>Accepter les conditions d'utilisation</li>
          </ul>
        </div>

        <div>
          <p className="font-medium">➤ Cas 2 : Déjà un compte particulier.urssaf.fr</p>
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            <li>Se connecter sur son espace</li>
            <li>Aller dans la rubrique "Intervenant"</li>
            <li>Autoriser Teach'r comme nouvel intervenant</li>
          </ul>
        </div>
      </div>

      {onRelance && (
        <ButtonField
          label="📧 Envoyer l'email de relance"
          variant="contained"
          onClick={() => {
            onRelance();
          }}
        />
      )}
    </div>
  );

  // Le menu « Actualiser » n'est pas au même endroit dans la liste et sur la fiche
  const refreshHint = context === 'page' ? (
    <p>
      Votre {getActiveClientBrandConfig()} a rempli le formulaire.
      Pour obtenir son état actuel et pouvoir déclarer ses cours, cliquez sur le bouton <strong className={primaryTextClass}>Actions</strong> de la fiche, puis sélectionnez <strong className={primaryTextClass}>Actualiser</strong>.
    </p>
  ) : (
    <p>
      Votre {getActiveClientBrandConfig()} a rempli le formulaire.
      Pour obtenir son état actuel et pouvoir déclarer ses cours, veuillez cliquer sur  <MoreVertIcon className={primaryTextClass} fontSize="small" /> dans la colonne <strong className={primaryTextClass}>Actions</strong> , puis sélectionner <strong className={primaryTextClass}>Actualiser</strong>.
    </p>
  );

  return {
    INVITATION_PENDING: {
      variant: 'action',
      title: 'Action requise',
      message: `Votre ${getActiveClientBrandConfig()} n'a pas encore complété son inscription. Pensez à le relancer.`,
    },
    CONTACT_COMPLETE: {
      variant: 'action',
      title: 'Action requise',
      message: urssafActivationMessage,
    },
    APPAREILLAGE_NF: {
      variant: 'action',
      title: 'Action requise',
      message: urssafActivationMessage,
    },
    MANDAT_ECHU: {
      variant: 'action',
      title: 'Action requise',
      message: `Le mandat SEPA de ce client est arrivé à échéance. Aucun prélèvement ne peut être effectué sans un mandat valide`,
    },
    APPAREILLAGE_EC: {
      variant: 'info',
      title: 'Traitement en cours',
      message: `La transmission de demande de paiement est en cours de traitement. Aucune action n'est requise pour le moment.`,
    },
    PENDING: {
      variant: 'info',
      title: 'Action requise',
      message: refreshHint,
    },
    APPAREILLAGE_VALIDE: {
      variant: 'success',
      title: 'Compte validé',
      message: `L'appareillage est validé. Vous pouvez déclarer des cours pour ce ${getActiveClientBrandConfig()}.`,
    },
    ACTIVE: {
      variant: 'success',
      title: 'Compte actif',
      message: null,
    },
    PARTICULIER_BLOQUE: {
      variant: 'error',
      title: 'Compte bloqué',
      message: description || `Le compte de ce ${getActiveClientBrandConfig()} est bloqué. Contactez le service client pour plus d'informations.`,
    },
    ERROR: {
      variant: 'error',
      title: 'Erreur TDP',
      message: description || `⚠️ Aucun client actif trouvé. Il est possible que cette personne ait résilié son inscription auprès de ce prestataire.`,
    },
  };
};

const BANNER_STYLES: Record<BannerVariant, { wrap: string; icon: string; title: string; text: string }> = {
  action: { wrap: 'bg-amber-50 border-amber-200', icon: '⚠️', title: 'text-amber-900', text: 'text-amber-800' },
  info: { wrap: 'bg-blue-50 border-blue-200', icon: 'ℹ️', title: 'text-blue-900', text: 'text-blue-800' },
  success: { wrap: 'bg-emerald-50 border-emerald-200', icon: '✅', title: 'text-emerald-900', text: 'text-emerald-800' },
  error: { wrap: 'bg-red-50 border-red-200', icon: '🚨', title: 'text-red-900', text: 'text-red-800' },
};

// ─── Composant ────────────────────────────────────────────────────────────────

interface ClientStatusBannerProps {
  status?: string | null;
  description?: string | null;
  onRelance?: () => void;
  context?: StatusBannerContext;
  className?: string;
}

/** Bannière contextuelle selon le statut du compte d'avance immédiate. */
export const ClientStatusBanner: React.FC<ClientStatusBannerProps> = ({
  status,
  description,
  onRelance,
  context = 'row',
  className = 'mx-4 mb-4 mt-3',
}) => {
  const { classes } = useTailwindBrand();
  if (!status) return null;

  const banner = buildStatusBanners({ description, onRelance, primaryTextClass: classes.primaryText, context })[status];
  if (!banner || (banner.variant === 'success' && !banner.message)) return null;

  const s = BANNER_STYLES[banner.variant];

  return (
    <div className={`${className} flex items-start gap-3 p-4 ${s.wrap} border rounded-lg`}>
      <span className="shrink-0 text-base leading-snug">{s.icon}</span>
      <div className={`text-sm ${s.text} min-w-0`}>
        <strong className={`font-semibold ${s.title}`}>{banner.title} : </strong>
        {banner.message}
      </div>
    </div>
  );
};

export default ClientStatusBanner;
